import type React from 'react';
import { useCallback, useState } from 'react';
import type { ListQuery } from '@/api/reference-admin';
import { CountriesTable, type CountryRow } from '@/components/countries-table';
import { CurrenciesTable, type CurrencyRow } from '@/components/currencies-table';
import type { DataTableResponse } from '@/components/data-table';
import { AddReferenceAdminModal } from '@/components/modals/add-reference-admin-modal';
import { type StateProvinceRow, StatesProvincesTable } from '@/components/states-provinces-table';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  countryEntityConfig,
  currencyEntityConfig,
  stateProvinceEntityConfig,
} from '@/config/reference-admin-entities';
import { type TabValue, useReferenceData } from '@/hooks/use-reference-data';

// Reference Admin specific defaults
const REF_ADMIN_DEFAULTS = {
  PAGE: 1,
  PAGE_SIZE: 10, // Optimized for reference data
  SORT_BY: 'name',
  ORDER: 'asc' as const,
} as const;

export const ReferenceAdminPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabValue>('countries');
  const [isTabSwitching, setIsTabSwitching] = useState(false);
  const [refFilters, setRefFilters] = useState<ListQuery>({
    page: REF_ADMIN_DEFAULTS.PAGE,
    pageSize: REF_ADMIN_DEFAULTS.PAGE_SIZE,
    sortBy: REF_ADMIN_DEFAULTS.SORT_BY,
    order: REF_ADMIN_DEFAULTS.ORDER,
  });

  // Use custom hook for data fetching
  const { tableData, isLoading, error, refetch } = useReferenceData(activeTab, refFilters);

  // Handlers for reference tables
  const handleRefFilterChange = useCallback((filterKey: string, value: string | string[]) => {
    setRefFilters((prev) => ({
      ...prev,
      page: REF_ADMIN_DEFAULTS.PAGE,
      [filterKey]: Array.isArray(value) && value.length === 0 ? undefined : value,
    }));
  }, []);

  const handleRefSortChange = useCallback((sortBy: string, order: 'asc' | 'desc') => {
    setRefFilters((prev) => ({
      ...prev,
      sortBy,
      order,
      page: REF_ADMIN_DEFAULTS.PAGE,
    }));
  }, []);

  const handleTableChange = useCallback((tab: TabValue) => {
    setIsTabSwitching(true);
    setActiveTab(tab);
    // Reset to default filter values when switching tables
    setRefFilters({
      page: REF_ADMIN_DEFAULTS.PAGE,
      pageSize: REF_ADMIN_DEFAULTS.PAGE_SIZE,
      sortBy: REF_ADMIN_DEFAULTS.SORT_BY,
      order: REF_ADMIN_DEFAULTS.ORDER,
    });

    // Clear tab switching state after a short delay
    setTimeout(() => setIsTabSwitching(false), 200);
  }, []);

  const handleRefPageChange = useCallback((page: number) => {
    setRefFilters((prev) => ({
      ...prev,
      page,
    }));
  }, []);

  const handleRefresh = useCallback(() => {
    // Reset filters to page 1 and trigger refetch
    setRefFilters((prev) => ({ ...prev, page: REF_ADMIN_DEFAULTS.PAGE }));
  }, []);

  const handleRetry = useCallback(() => {
    refetch();
  }, [refetch]);

  // Get display text for current tab
  const getTabDisplayText = useCallback((tab: TabValue) => {
    switch (tab) {
      case 'countries':
        return { singular: 'country', plural: 'countries', title: 'Countries' };
      case 'states':
        return {
          singular: 'state/province',
          plural: 'states/provinces',
          title: 'States & Provinces',
        };
      case 'currencies':
        return { singular: 'currency', plural: 'currencies', title: 'Currencies' };
      default:
        return { singular: 'item', plural: 'items', title: 'Items' };
    }
  }, []);

  // Get entity config for current tab
  const getCurrentEntityConfig = useCallback((tab: TabValue) => {
    switch (tab) {
      case 'countries':
        return countryEntityConfig;
      case 'states':
        return stateProvinceEntityConfig;
      case 'currencies':
        return currencyEntityConfig;
      default:
        return null;
    }
  }, []);

  const tabDisplay = getTabDisplayText(activeTab);
  const currentEntityConfig = getCurrentEntityConfig(activeTab);

  // Determine loading state (data loading or tab switching)
  const showLoading = isLoading || isTabSwitching;

  // Determine if there's an error to display
  const displayError = error;

  return (
    <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
      {/* Screen reader announcements */}
      <div className="sr-only" aria-live="polite" aria-atomic="true">
        {showLoading
          ? `Loading ${tabDisplay.plural} data...`
          : displayError
            ? `Error loading data: ${displayError}`
            : tableData?.pagination
              ? `${tabDisplay.title} data loaded. ${tableData.pagination.total || 0} ${
                  tableData.pagination.total === 1 ? tabDisplay.singular : tabDisplay.plural
                } found.`
              : `${tabDisplay.title} data loaded.`}
      </div>

      <div className="px-4 lg:px-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold tracking-tight">Reference Admin</h1>
        </div>
      </div>

      <div className="px-4 lg:px-6">
        {displayError && !isLoading ? (
          <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4">
            <div className="flex items-center justify-between">
              <span className="text-destructive">{displayError}</span>
              <Button variant="outline" size="sm" onClick={handleRetry}>
                Retry
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">{tabDisplay.title}</h2>
              <div className="flex items-center gap-2">
                <label htmlFor="table-selector" className="text-sm font-medium">
                  Table:
                </label>
                <Select
                  value={activeTab}
                  onValueChange={(v) => handleTableChange(v as TabValue)}
                  aria-label="Select reference data table"
                  disabled={isTabSwitching}
                >
                  <SelectTrigger
                    className="w-[200px]"
                    id="table-selector"
                    aria-describedby="table-selector-description"
                  >
                    <SelectValue placeholder="Select a table" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="countries">Countries</SelectItem>
                    <SelectItem value="states">States/Provinces</SelectItem>
                    <SelectItem value="currencies">Currencies</SelectItem>
                  </SelectContent>
                </Select>
                <div id="table-selector-description" className="sr-only">
                  Choose which reference data table to view and manage
                </div>
              </div>
            </div>

            {activeTab === 'countries' && (
              <CountriesTable
                data={tableData as DataTableResponse<CountryRow>}
                isLoading={showLoading}
                error={displayError}
                onFilterChange={handleRefFilterChange}
                onSortChange={handleRefSortChange}
                onPageChange={handleRefPageChange}
                onRefresh={handleRefresh}
                headerActions={
                  currentEntityConfig && (
                    <AddReferenceAdminModal
                      config={currentEntityConfig}
                      onSuccess={() => {
                        // Refresh the data after successful creation
                        handleRefresh();
                      }}
                    />
                  )
                }
              />
            )}
            {activeTab === 'states' && (
              <StatesProvincesTable
                data={tableData as DataTableResponse<StateProvinceRow>}
                isLoading={showLoading}
                error={displayError}
                onFilterChange={handleRefFilterChange}
                onSortChange={handleRefSortChange}
                onPageChange={handleRefPageChange}
                onRefresh={handleRefresh}
                headerActions={
                  currentEntityConfig && (
                    <AddReferenceAdminModal
                      config={currentEntityConfig}
                      onSuccess={() => {
                        // Refresh the data after successful creation
                        handleRefresh();
                      }}
                    />
                  )
                }
              />
            )}
            {activeTab === 'currencies' && (
              <CurrenciesTable
                data={tableData as DataTableResponse<CurrencyRow>}
                isLoading={showLoading}
                error={displayError}
                onFilterChange={handleRefFilterChange}
                onSortChange={handleRefSortChange}
                onPageChange={handleRefPageChange}
                onRefresh={handleRefresh}
                headerActions={
                  currentEntityConfig && (
                    <AddReferenceAdminModal
                      config={currentEntityConfig}
                      onSuccess={() => {
                        // Refresh the data after successful creation
                        handleRefresh();
                      }}
                    />
                  )
                }
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};
